<template>
  <fts-design-modal
    width="1024px"
    append-header-class="module-import-design-modal__header"
    append-content-class="module-import-design-modal__body"
    append-footer-class="module-import-design-modal__footer"
    class="fts-module-import-design-modal"
    ref="ftsModuleImportDesignModal"
    @closed="reset"
  >
    <!-- header -->
    <template #header>
      <fts-btn
        lg
        text
        icon
        :disabled="submitting"
        @click="beforeClose"
      >
        <font-awesome-icon :icon="['fas', 'times']" />
      </fts-btn>
    </template>
    <!-- body -->
    <template #body>
      <!-- preview -->
      <template v-if="isPreview">
        <!-- sticky container -->
        <div class="mid-sticky-container">
          <!-- module -->
          <div class="design-modal-content__header">
            <div class="design-modal-content__header-title">
              Looking good. Your app is just one click away
            </div>
            <div class="design-modal-content__header-description">
              Inistate has gone through the spreadsheet, we are good to go. 
              <br/>
              Complete module name below, you may change the information name and type if necessary
            </div>
          </div>
          <!-- module card -->
          <div class="mid-module-card">
            <div class="mid-module-card-form-group">
              <fts-emoji
                :toggle-size="35"
                v-model="formData.emoji"
                :disabled="submitting"
              />
              <input
                type="text"
                placeholder="Please enter module name"
                class="mid-module-card-form-control fts-form-control"
                :class="{error: $v.formData.name && $v.formData.name.$error}"
                v-model="formData.name"
                spellcheck="false"
                :disabled="submitting"
                @input="dispatchAutofill"
                @blur="$v.formData.name.$touch()"
              />
            </div>
            <div class="design-modal-form-error" v-if="moduleNameError()">{{moduleNameError()}}</div>
            <div class="mid-module-card-description">
              module is a minimum and an essential unit that can be created in Workspace.
              <a
                href="https://community.fetias.com/t/fetias101-module/79/2"
                class="fts-link-primary"
                target="_blank"
              >
                Learn more
              </a>
            </div>
          </div>
        </div>
        <!-- import data -->
        <div class="mid-data">
          <!-- header -->
          <div class="mid-data__header">
            <div class="mid-data-col">
              <div class="mid-data-col__l">
                <div class="mid-data__header-icon-wrapper">
                  <font-awesome-icon :icon="['fas', 'table']" class="mid-data__header-icon" />
                  <div class="mid-data__header-file">
                    <div
                      class="mid-data__header-file-name"
                      v-if="uploadFormData.importExcel && uploadFormData.importExcel.name"
                    >{{uploadFormData.importExcel.name}}</div>
                    <div
                      class="mid-data__header-file-count"
                    >
                      Total records: {{formData.meta.total}}
                    </div>
                  </div>
                </div>
                <font-awesome-icon :icon="['fas', 'long-arrow-alt-right']" class="mid-data-arrow" />
              </div>
              <div class="mid-data-col__r">
                <img
                  src="../../assets/images/logo/inistate-logo-full.png"
                  class="mid-data__header-logo"
                />
              </div>
            </div>
          </div>
          <!-- loading -->
          <div class="mid-loading" v-if="submitting">
            <fts-spinner show class="mid-loading-icon" />
            <div class="mid-loading-label">
              Importing data...
            </div>
          </div>
          <!-- content -->
          <div class="mid-data__content" v-else>
            <div class="mid-data-col" v-for="x in formData.information" :key="x.id">
              <div class="mid-data-col__l">
                <fts-label-group
                  :item="x.raw"
                  icon-key="type"
                  label-key="displayName"
                  class="mid-data-raw"
                />
                <font-awesome-icon :icon="['fas', 'long-arrow-alt-right']" class="mid-data-arrow" />
              </div>
              <div class="mid-data-col__r">
                <fts-label-group
                  :item="x.value"
                  icon-key="type"
                  label-key="displayName"
                  class="mid-data-output"
                  @click.native="openInformationModal(x.value, x.raw.type)"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
      <!-- import -->
      <template v-else>
        <div class="design-modal-content__header">
          <div class="design-modal-content__header-title">
            Prepare your spreadsheet
          </div>
          <div class="design-modal-content__header-description">
            Convert spreadsheet into web, desktop and mobile apps at the same time in one step
          </div>
        </div>
        <div class="mid-import-wrapper">
          <!-- tutorial -->
          <div class="mid-import-tutorial">
            <img src="../../assets/images/studio/spreadsheet.png" class="mid-import-tutorial-img" />
          </div>
          <!-- file upload -->
          <fts-file-upload
            :item="{
              fieldName: 'importExcel',
            }"
            :form-data="uploadFormData"
            :additional-form-data="{
              import: 'import',
              vectorId: null,
            }"
            comfort-design
            class="mid-import-upload"
            @fileUploaded="uploadFile"
          />
          <!-- error -->
          <div class="mid-import-error" v-if="uploadError">
            {{uploadError}}
          </div>
        </div>
      </template>
      <!-- information design modal -->
      <fts-information-design-modal disable-continue ref="ftsInformationDesignModal" />
    </template>
    <!-- footer:left -->
    <template #footerLeft v-if="!submitting">
      <fts-btn outline @click="resetUploadData" v-if="isPreview">
        Back
      </fts-btn>
      <!-- <fts-btn @click="test">Test</fts-btn> -->
    </template>
    <!-- footer:right -->
    <template #footerRight>
      <!-- cancel -->
      <fts-btn text @click="beforeClose" v-if="!submitting">
        Cancel
      </fts-btn>
      <!-- done -->
      <fts-btn
        outline
        color="primary"
        :loading="submitting"
        @click="done"
        v-if="isPreview"
      >
        <font-awesome-icon :icon="['fas', 'check']" />Create App
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import changesets from "diff-json";

import commons from "../commons/commons";
import designValidator from "../commons/designValidator";
import { store as canvasDesignStore } from "../store/canvasDesignStore";
import { classificationStore } from "../store/classificationStore";

import FTSDesignModal from "./FTSDesignModal";
import FTSInformationDesignModal from "./FTSInformationDesignModal";
import FTSBtn from "../elements/FTSBtn";
import FTSEmoji from "../elements/FTSEmoji";
import FTSFileUpload from "../elements/FTSFileUpload";
import FTSLabelGroup from "../elements/display/FTSLabelGroup";
import FTSSpinner from "../elements/FTSSpinner";

export default {
  components: {
    "fts-design-modal": FTSDesignModal,
    "fts-information-design-modal": FTSInformationDesignModal,
    "fts-btn": FTSBtn,
    "fts-emoji": FTSEmoji,
    "fts-file-upload": FTSFileUpload,
    "fts-label-group": FTSLabelGroup,
    "fts-spinner": FTSSpinner,
  },
  data: () => ({
    formData: null,
    originalData: null,
    resolve: null,
    uploadFormData: null,
    uploadError: null,
    submitting: false,
  }),
  validations() {
    let formData = {};
    formData.name = designValidator.moduleDesign(this.workspaceModules).name;
    return { formData };
  },
  methods: {
    async show() {
      this.formData = {};
      this.uploadFormData = {};

      return new Promise(resolve => {
        this.resolve = resolve;
        this.$refs.ftsModuleImportDesignModal.show({
          beforeCloseHandler: () => {
            if (this.submitting) return;
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.close();
                    }
                  }
                ]
              });
            } else {
              this.close();
            }
          }
        });
        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
        });
      });
    },
    beforeClose() {
      this.$refs.ftsModuleImportDesignModal.beforeClose();
    },
    close() {
      this.$refs.ftsModuleImportDesignModal.close();
    },
    reset() {
      this.resolve && this.resolve(false);
      this.$v.$reset();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    async done() {
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        this.submitting = true;
        const submitData = _.cloneDeep(this.formData);
        try {
          let response = await classificationStore.dispatch('importData', {
            name: submitData.name,
            emoji: submitData.emoji,
            information: submitData.information.map(x => ({...x.value})),
            url: submitData.url,
            submit: true
          });

          this.resolve && this.resolve(response.data.id); 
          this.close();
        } catch(err) {
          this.$toasted.global.error({
            response: err.response,
          });
        } finally {
          this.submitting = true;
        }
      }
    },
    debounceInput: _.debounce(async vm => {
      try {
        const res = await canvasDesignStore.dispatch("ai", {
          type: "emoji",
          text: vm.formData && vm.formData.name
        });
        if (res.data && vm.formData) {
          vm.$set(vm.formData, "emoji", res.data);
        }
      } catch (err) {
        console.error(err);
      }
    }, 800),
    dispatchAutofill() {
      this.debounceInput(this);
    },
    moduleNameError() {
      return designValidator.moduleDesignError(this.$v.formData, "name");
    },
    async openInformationModal(item, type) {
      const getInformationTypes = () => {
        try {
          return commons
            .expandInformationType(type)
            .map(x => commons.informationTypes.find(y => y.id === x))
            .sort((x, y) => x.id - y.id);
        } catch (err) {
          console.error(err);
        }
      };

      const res = await this.$refs.ftsInformationDesignModal.show(
        item,
        null,
        null,
        {
          information: this.formData.information.map(x => ({
            ...x.value
          })) /* to-do */,
          informationTypes: getInformationTypes()
        }
      );

      if (res != null && res != true) {
        const findIndex = this.formData.information.findIndex(
          x => x.value.id === item.id
        );
        if (findIndex > -1) {
          this.$set(this.formData.information[findIndex], "value", res);
        }
      }
    },
    async uploadFile(response) {
      this.uploadError = null;

      try {
        const res = await classificationStore.dispatch("importData", {
          url: response.url,
          formData: this.uploadFormData
        });

        if (res.data.meta) {
          const information = res.data.meta.information.map(x => ({
            id: x.id,
            raw: x,
            value: x
          }));

          this.$set(this.formData, "information", information);
          this.$set(this.formData, "url", response.url);
          this.$set(this.formData, "meta", res.data.meta);
        }
      } catch (err) {
        let error = 'Something went wrong';
        if (err.response && err.response.data && err.response.data.message) {
          error = err.response.data.message
        }
        this.uploadError = error;
        this.$delete(this.uploadFormData, "importExcel");
      }
    },
    async test() {
      let data = {
        url: "ts/7cd9d38b1688445189f38ac6482f6ca0-FETIAS-Export-Employee.xlsx",
        formData: {
          importExcel: {
            path: "ts/7cd9d38b1688445189f38ac6482f6ca0-FETIAS-Export-Employee.xlsx",
            name: "FETIAS-Export-Employee.xlsx",
            size: 10426
          }
        }
      };

      const res = await classificationStore.dispatch("importData", {
          url: data.url,
          formData: data.formData
        });

        if (res.data.meta) {
          const information = res.data.meta.information.map(x => ({
            id: x.id,
            raw: x,
            value: x
          }));

          this.$set(this.formData, "information", information);
          this.$set(this.formData, "url", data.url);
          this.$set(this.formData, "meta", res.data.meta);
        }
    },
    resetUploadData() {
      this.uploadFormData = {};
      this.$delete(this.formData, "information");
    }
  },
  computed: {
    workspaceModules() {
      return canvasDesignStore.getters.workspaceModules;
    },
    isPreview() {
      return this.formData.information && this.formData.information.length;
    }
  }
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-module-import-design-modal {
  .module-import-design-modal__header {
    justify-content: flex-end;
    padding: 0.75rem;
  }
  .module-import-design-modal__body {
    padding: 0 1.5rem 1.5rem;
  }
  .module-import-design-modal__footer {
    padding-bottom: 1.5rem;
    background-color: var(--fts-background);
  }
  /* sticky container */
  .mid-sticky-container {
    position: sticky;
    position: -webkit-sticky;
    top: 0;
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    background-color: var(--fts-background);
  }
  /* module card */
  .mid-module-card {
    padding: 1rem 1rem 1.5rem;
    border-radius: 0.5rem;
    background-color: var(--fts-primary-light);
    background-size: cover;
    background-image: url("../../assets/images/studio/mesh-gradient.png");
    .mid-module-card-form-group {
      display: flex;
      align-items: center;
    }
    .mid-module-card-form-control {
      @include theme() {
        @include placeholder(
          $fontSize: inherit,
          $fontWeight: inherit,
          $color: rgba(theme-get("fts-primary"), 0.45)
        );
      }
      box-shadow: none;
      border: 0;
      background-color: transparent;
      color: var(--fts-primary);
      font-weight: 700;
      font-size: 2.5rem;
    }
    .mid-module-card-description {
      font-size: 0.875rem;
      line-height: 1.25rem;
      color: var(--fts-gray-6);
    }
  }
  /* import */
  .mid-import-wrapper {
    max-width: 36rem;
    margin-left: auto;
    margin-right: auto;
  }
  .mid-import-tutorial {
    max-height: 25rem;
    margin-bottom: 1rem;
    text-align: center;
    .mid-import-tutorial-img {
      max-height: inherit;
      max-width: 100%;
      width: auto;
      border-radius: 0.25rem;
    }
  }
  .mid-import-upload.fts-file-upload 
  .file-upload__header
  .file-upload-uploader {
    .fts-file-dropzone {
      &:hover {
        @include theme() {
          background-color: rgba(theme-get('fts-secondary'), .095);
        }
        border-color: var(--fts-secondary);
        .dropzone-icon,
        .dropzone-label {
          color: var(--fts-secondary);
        }
      }
      .dropzone-icon,
      .dropzone-label {
        color: var(--fts-secondary);
      }
    }
  }
  /* import error */
  .mid-import-error {
    @include theme() {
      background-color: rgba(theme-get('fts-danger'), .05);
    }
    margin-top: 1.5rem;
    padding: 1.5rem;
    border-radius: .375rem;
    color: var(--fts-danger);
    text-align: center;
  }
  /* data */
  .mid-data {
    .mid-data-arrow {
      margin-left: 1rem;
      margin-right: 1rem;
      color: var(--fts-gray-5);
      font-size: 1.125rem;
    }
    .mid-data-col {
      display: flex;
      align-items: center;
      .mid-data-col__l {
        flex: 1 0 50%;
        max-width: 50%;
        display: flex;
        align-items: center;
      }
      .mid-data-col__r {
        flex: 1 0 50%;
        max-width: 50%;
      }
    }
    .mid-data__header {
      margin-bottom: 1.5rem;
      .mid-data-col__l {
        .mid-data__header-icon-wrapper {
          flex: 1;
          min-width: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          .mid-data__header-icon {
            margin-right: 0.75rem;
          }
          .mid-data__header-file {
            min-width: 0;
            font-size: 1rem;
            .mid-data__header-file-name {
              @include text-overflow-ellipsis;
            }
            .mid-data__header-file-count {
              color: var(--fts-gray-5);
            }
          }
        }
      }
      .mid-data-col__r {
        text-align: center;
      }
      .mid-data__header-icon {
        font-size: 2.5rem;
        color: var(--fts-primary);
      }
      .mid-data__header-logo {
        height: 2.5rem;
        vertical-align: middle;
      }
    }
    .mid-data__content {
      .mid-data-col {
        &:not(:last-child) {
          margin-bottom: 1rem;
        }
      }
      .mid-data-raw {
        flex: 1;
        padding: 0.75rem;
        border-radius: 0.5rem;
        border: 1px solid var(--fts-gray-3);
      }
      .mid-data-output {
        &:hover {
          @include theme() {
            background-color: rgba(theme-get("fts-primary"), 0.095);
          }
        }
        padding: 0.75rem;
        border-radius: 0.5rem;
        border: 1px solid var(--fts-primary);
        cursor: pointer;
      }
    }
  }
  /* loading */
  .mid-loading {
    padding: 3rem;
    text-align: center;
    .mid-loading-icon {
      margin-bottom: 1rem;
    }
    .mid-loading-label {
      font-size: 1rem;
      line-height: 1.5rem;
    }
  }
}
</style>